import React from 'react';
import { useVisibleTicker } from '../useVisibleTicker';

/** 取自 App 内实际主色（进度条、图表、分类标签） */
/* 各产品品牌色（原为 #7C5CE6）统一收到强调色：
   五个演示在同一页滚动出现，各用自己的色会让强调色失去指示作用。
   SVG 的 stroke/fill 不吃 CSS 变量的回退链，这里取字面值。 */
const BRAND = '#ff6308';

/**
 * 演示用的订阅样本。金额取通用品类而非具体品牌，
 * 但月付/年付两种周期都保留 —— 这是换算引擎存在的理由。
 */
const ITEMS = [
  { name: '视频会员', cycle: '月付', monthly: 25, yearly: 300 },
  { name: '音乐会员', cycle: '月付', monthly: 11, yearly: 132 },
  { name: '云存储', cycle: '月付', monthly: 21, yearly: 252 },
  { name: '健身年卡', cycle: '年付', monthly: 150, yearly: 1800 },
];

const MONTHLY_TOTAL = 840;
const YEARLY_TOTAL = MONTHLY_TOTAL * 12;

/**
 * 订无忧模拟界面（手机形态）。
 *
 * 演示的落点是产品最硬的那条工程约束：月度与年度金额由同一个换算引擎输出，
 * 页面不各算一套。所以这里让视图在月/年之间轮转，两个口径的数字同时可见 ——
 * 年付项在月度视图里换算成每月金额，月付项在年度视图里乘 12，总额始终 ×12 对得上。
 * 这比放一句"数据准确"的文案更能说明问题。
 *
 * 产品处于 restructuring 状态，界面上如实标注，避免访客以为现在可下载。
 */
const SubscriptionDemo: React.FC = () => {
  const { ref, tick } = useVisibleTicker(3200);
  const yearlyView = tick % 2 === 1;

  const total = yearlyView ? YEARLY_TOTAL : MONTHLY_TOTAL;
  const counterTotal = yearlyView ? MONTHLY_TOTAL : YEARLY_TOTAL;

  return (
    <div ref={ref} className="flex justify-center">
      <div className="sx-device sx-device-phone w-[248px] shrink-0">
        <div
          className="relative rounded-[22px] overflow-hidden flex flex-col"
          style={{ background: '#0b0b0c', aspectRatio: '232 / 400' }}
        >
          <div
            className="flex items-center justify-between px-3.5 py-3 shrink-0"
            style={{ borderBottom: '1px solid var(--sx-line)' }}
          >
            <span className="text-[11px] font-medium text-white">订阅管理</span>
            <span className="sx-mono text-[7px]" style={{ color: BRAND }}>
              升级重构中
            </span>
          </div>

          {/* 口径切换。两个视图共用一份数据，切换只改变呈现单位 */}
          <div className="px-3.5 pt-3 shrink-0">
            <div
              className="flex items-center gap-0.5 p-0.5 rounded-full w-fit"
              style={{ background: 'rgba(255,255,255,0.05)' }}
            >
              {['月度', '年度'].map((label, index) => {
                const active = (index === 1) === yearlyView;
                return (
                  <span
                    key={label}
                    className="sx-mono sx-mono-plain text-[7px] px-2 py-[3px] rounded-full transition-all duration-500"
                    style={{
                      background: active ? BRAND : 'transparent',
                      color: active ? '#fff' : 'var(--sx-mute)',
                    }}
                  >
                    {label}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="px-3.5 pt-2.5 pb-3 shrink-0">
            <p className="sx-num text-[22px] font-medium text-white leading-none tabular-nums">
              ¥{total.toLocaleString()}
            </p>
            <p className="sx-mono sx-mono-plain text-[8px] mt-1.5">
              {yearlyView ? '月度约' : '年度约'} ¥{counterTotal.toLocaleString()}
            </p>
            <div className="flex items-center gap-1.5 mt-2">
              <span className="sx-dot sx-dot-live" style={{ background: BRAND }} aria-hidden="true" />
              <span className="sx-mono text-[8px]">12 项在用 · 4 项即将到期</span>
            </div>
          </div>

          <div className="flex-1 px-3 space-y-1 overflow-hidden">
            {ITEMS.map((item) => (
              <div
                key={item.name}
                className="rounded-[9px] px-2.5 py-[7px] flex items-center justify-between gap-2"
                style={{ border: '1px solid var(--sx-line)' }}
              >
                <div className="min-w-0">
                  <p className="text-[10px] text-white truncate leading-tight">{item.name}</p>
                  <p className="sx-mono sx-mono-plain text-[7px] mt-0.5">{item.cycle}</p>
                </div>
                <p
                  className="sx-num text-[10px] shrink-0 tabular-nums transition-colors duration-500"
                  style={{ color: 'var(--sx-dim)' }}
                >
                  ¥{yearlyView ? item.yearly.toLocaleString() : item.monthly}
                  <span className="sx-mono sx-mono-plain text-[7px]" style={{ color: 'var(--sx-mute)' }}>
                    /{yearlyView ? '年' : '月'}
                  </span>
                </p>
              </div>
            ))}
          </div>

          <div className="px-3 pb-3 pt-2 shrink-0" style={{ borderTop: '1px solid var(--sx-line)' }}>
            <p className="sx-mono text-[7px] text-center">月/年换算由同一引擎输出</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDemo;
